import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";
import ChartPanel from "./ChartPanel";

// Mirrors the badge colours in components/WorkOrderStatus, so a slice is the
// same colour as the chip the customer sees on their work-order board.
// Tailwind tokens there resolve to: secondary #FF5F00, blue-600 #2563EB,
// purple-600 #9333EA.
const STATUS_COLORS = {
  PENDING_APPROVAL: "#4F46E5",
  PUBLISHED: "#FF5F00",
  ROUTED: "#2196F3",
  ASSIGNED: "#2561A9",
  CONFIRM: "#5D7464",
  INPROGRESS: "#FF9800",
  CHECK_IN: "#0074A1",
  CHECK_OUT: "#673AB7",
  INCOMPLETE: "#F44336",
  WORKDONE: "#295E80",
  VERIFIED: "#279AA0",
  APPROVED: "#268E2A",
  INVOICED: "#2563EB",
  PAID: "#0F40AE",
  CANCELLED: "#F44336",
  CANCELLED_PAYABLE: "#9333EA",
};

// Customer-facing wording where the raw status name is not what the customer
// knows the stage as. Anything unlisted falls back to the humanised enum name.
const STATUS_LABELS = {
  // The ticket is raised and waiting on the service provider — "Submitted" to
  // the customer, matching the badge on their work-order board.
  PENDING_APPROVAL: "Submitted",
  // The service provider accepted the ticket and put it to market — "Accepted"
  // to the customer, as on the KPI card and the board badge.
  PUBLISHED: "Accepted",
};

const humanise = (status) =>
  STATUS_LABELS[status] ??
  status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const StatusDistributionChart = ({ statusDistribution = [], isLoading, error }) => {
  const options = useMemo(
    () => ({
      chart: { type: "pie", height: 320 },
      title: { text: null },
      tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
      plotOptions: {
        pie: {
          innerSize: "62%",
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            format: "{point.name}: {point.y}",
            style: { fontSize: "11px", fontWeight: "400", textOutline: "none", color: "#6B7280" },
          },
        },
      },
      series: [
        {
          name: "Work Orders",
          data: statusDistribution.map((slice) => ({
            name: humanise(slice.status),
            y: slice.count,
            color: STATUS_COLORS[slice.status] ?? "#90A4AE",
          })),
        },
      ],
    }),
    [statusDistribution],
  );

  return (
    <ChartPanel
      title="Status Distribution"
      subtitle="Where your work orders stand"
      icon="fa-solid fa-chart-pie"
      accent="#0891B2"
      accentBg="#ECFEFF"
      help="Distribution of work orders by their current status, for work scheduled to start within the selected date range. Excludes work orders in draft."
      isLoading={isLoading}
      error={error}
      isEmpty={statusDistribution.length === 0}
      emptyMessage="Nothing to break down yet."
    >
      <BaseChart options={options} />
    </ChartPanel>
  );
};

export default StatusDistributionChart;
